import { useState, useEffect, useCallback } from 'react';
import {
  Plane,
  Plus,
  Trash2,
  X,
  Calendar,
  Phone,
  FileText,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
} from 'lucide-react';
import { accessApi } from '../services/accessApi';
import { useAuth } from '../contexts/AuthContext';
import { BackHeader } from '../components/BackHeader';

interface TravelNotice {
  id: string;
  startDate: string;
  endDate: string;
  emergencyContact: string;
  notes?: string;
  status: 'active' | 'cancelled' | 'past';
  createdAt: string;
}

const emptyForm = {
  startDate: '',
  endDate: '',
  emergencyContact: '',
  notes: '',
};

export default function TravelNotices() {
  const { user } = useAuth();
  const [notices, setNotices] = useState<TravelNotice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const fetchNotices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await accessApi.travel.list();
      const payload = res.data?.data ?? res.data;
      const list = payload?.notices || payload?.data?.notices || (Array.isArray(payload) ? payload : []);
      const normalized = Array.isArray(list) ? list.map((n: any) => ({
        ...n,
        startDate: n.start_date || n.startDate || '',
        endDate: n.end_date || n.endDate || '',
        emergencyContact: n.emergency_contact || n.emergencyContact || '',
        createdAt: n.created_at || n.createdAt || '',
        status: n.status || 'active',
      })) : [];
      setNotices(normalized);
    } catch (err: any) {
      setError(err?.message || 'Error al cargar avisos de viaje');
      setNotices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.startDate || !form.endDate || !form.emergencyContact.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await accessApi.travel.create({
        condoId: user?.condoId || (user as any)?.condo_id || localStorage.getItem('condoId') || 'condo-default',
        startDate: form.startDate,
        start_date: form.startDate,
        endDate: form.endDate,
        end_date: form.endDate,
        emergencyContact: form.emergencyContact.trim(),
        emergency_contact: form.emergencyContact.trim(),
        notes: form.notes.trim() || undefined,
      });
      setSuccessMsg('Aviso de viaje creado correctamente');
      setForm(emptyForm);
      setShowForm(false);
      fetchNotices();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Error al crear aviso');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id: string) => {
    if (!confirm('¿Cancelar este aviso de viaje?')) return;
    try {
      await accessApi.travel.cancel(id);
      setNotices((prev) => prev.filter((n) => n.id !== id));
      setSuccessMsg('Aviso cancelado');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Error al cancelar aviso');
    }
  };

  const isPast = (endDate: string) => new Date(endDate) < new Date();
  const isActive = (n: TravelNotice) =>
    n.status !== 'cancelled' && !isPast(n.endDate);

  const activeNotices = notices.filter(isActive);
  const pastNotices = notices.filter((n) => !isActive(n));

  const renderNotice = (notice: TravelNotice) => {
    const past = isPast(notice.endDate);
    const cancelled = notice.status === 'cancelled';
    return (
      <div
        key={notice.id}
        className={`bg-slate-50 rounded-lg p-4 border ${
          cancelled ? 'border-red-200 opacity-60' : past ? 'border-slate-200 opacity-75' : 'border-slate-200'
        }`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              cancelled ? 'bg-red-100' : past ? 'bg-slate-200' : 'bg-blue-100'
            }`}>
              <Plane className={`w-5 h-5 ${
                cancelled ? 'text-red-500' : past ? 'text-slate-400' : 'text-blue-600'
              }`} />
            </div>
            <div className="space-y-1 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-slate-900">
                  {new Date(notice.startDate).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                  {' → '}
                  {new Date(notice.endDate).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  cancelled
                    ? 'bg-red-100 text-red-700'
                    : past
                    ? 'bg-gray-100 text-gray-500'
                    : 'bg-green-100 text-green-700'
                }`}>
                  {cancelled ? 'Cancelado' : past ? 'Finalizado' : 'Activo'}
                </span>
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <Phone className="w-3 h-3" />
                Contacto: {notice.emergencyContact}
              </div>
              {notice.notes && (
                <div className="flex items-start gap-1 text-sm text-slate-600 mt-1">
                  <FileText className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>{notice.notes}</span>
                </div>
              )}
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <Clock className="w-3 h-3" />
                Creado: {new Date(notice.createdAt).toLocaleDateString('es-MX')}
              </div>
            </div>
          </div>
          {!cancelled && !past && (
            <button
              onClick={() => handleCancel(notice.id)}
              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Cancelar aviso"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)] p-4 md:p-6">
      <BackHeader fallback="/" />
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Plane className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Avisos de Viaje</h1>
            <p className="text-sm text-slate-500">Notifica tus ausencias al condominio</p>
          </div>
        </div>

        {/* Success */}
        {successMsg && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm font-medium">{successMsg}</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Create Button */}
        <div className="flex justify-end">
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Crear Aviso de Viaje
          </button>
        </div>

        {/* Create Form */}
        {showForm && (
          <form
            onSubmit={handleCreate}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fecha de salida *</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="date"
                    required
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fecha de regreso *</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="date"
                    required
                    min={form.startDate}
                    value={form.endDate}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contacto de emergencia *</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  required
                  value={form.emergencyContact}
                  onChange={(e) => setForm({ ...form, emergencyContact: e.target.value })}
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nombre y teléfono"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notas</label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Información adicional para seguridad..."
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {submitting ? 'Guardando...' : 'Guardar Aviso'}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setForm(emptyForm); }}
                className="px-4 py-2 border border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-slate-100"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}

        {/* Active Notices */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Avisos activos</h2>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : activeNotices.length === 0 ? (
            <div className="text-center py-8">
              <Plane className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">No tienes avisos de viaje activos</p>
              <p className="text-slate-400 text-xs mt-1">Crea un aviso cuando planees estar fuera</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeNotices.map(renderNotice)}
            </div>
          )}
        </div>

        {/* Past Notices */}
        {pastNotices.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Historial</h2>
            <div className="space-y-3">
              {pastNotices.map(renderNotice)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
